/**
 * Domain → Track → Module → Scene cascade picker for analytics / usage.
 */
import React, { useEffect, useState } from 'react';
import { coachAPI, sceneAPI } from '../../services/api';

const selectStyle = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: 14,
  boxSizing: 'border-box',
  background: '#fff',
};

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 4,
};

/**
 * @param {object} props
 * @param {string[]} [props.domains] — domain_id strings or {domain_id, display_name}
 * @param {string} [props.initialDomain]
 * @param {boolean} [props.requireScene=true]
 * @param {(sel: {domainId, trackId, moduleId, sceneId}) => void} props.onChange
 * @param {object} [props.style]
 */
export default function CurriculumScenePicker({
  domains = [],
  initialDomain = '',
  requireScene = true,
  onChange,
  style,
}) {
  const [domainId, setDomainId] = useState(initialDomain || '');
  const [trackId, setTrackId] = useState('');
  const [moduleId, setModuleId] = useState('');
  const [sceneId, setSceneId] = useState('');
  const [tracks, setTracks] = useState([]);
  const [modules, setModules] = useState([]);
  const [scenes, setScenes] = useState([]);

  const domainOptions = (domains || []).map((d) => (
    typeof d === 'string'
      ? { domain_id: d, display_name: d }
      : { domain_id: d.domain_id || d.id, display_name: d.display_name || d.domain_id || d.id }
  )).filter((d) => d.domain_id);

  useEffect(() => {
    if (!domainId && domainOptions.length) {
      setDomainId(domainOptions[0].domain_id);
    }
  }, [domainOptions, domainId]);

  useEffect(() => {
    if (!domainId) {
      setTracks([]); setTrackId(''); setModules([]); setModuleId(''); setScenes([]); setSceneId('');
      return;
    }
    let cancelled = false;
    coachAPI.listTracks(domainId)
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.tracks || data || [];
        setTracks(Array.isArray(list) ? list : []);
        setTrackId('');
        setModules([]);
        setModuleId('');
        setScenes([]);
        setSceneId('');
      })
      .catch(() => { if (!cancelled) setTracks([]); });
    return () => { cancelled = true; };
  }, [domainId]);

  useEffect(() => {
    if (!domainId || !trackId) {
      setModules([]); setModuleId(''); setScenes([]); setSceneId('');
      return;
    }
    let cancelled = false;
    coachAPI.listModules(domainId, trackId)
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.modules || data || [];
        setModules(Array.isArray(list) ? list : []);
        setModuleId('');
        setScenes([]);
        setSceneId('');
      })
      .catch(() => { if (!cancelled) setModules([]); });
    return () => { cancelled = true; };
  }, [domainId, trackId]);

  useEffect(() => {
    if (!domainId) {
      setScenes([]); setSceneId('');
      return;
    }
    let cancelled = false;
    const params = { domain: domainId, limit: 200 };
    if (moduleId) params.module_id = moduleId;
    sceneAPI.list(params)
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.scenes || data || [];
        setScenes(Array.isArray(list) ? list : []);
        setSceneId('');
      })
      .catch(() => { if (!cancelled) setScenes([]); });
    return () => { cancelled = true; };
  }, [domainId, moduleId]);

  useEffect(() => {
    if (typeof onChange === 'function') {
      onChange({ domainId, trackId, moduleId, sceneId });
    }
    // Intentionally omit onChange from deps — parents often pass inline handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId, trackId, moduleId, sceneId]);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 10,
      ...style,
    }}>
      <label style={labelStyle}>
        Domain
        <select value={domainId} onChange={(e) => setDomainId(e.target.value)} style={selectStyle}>
          <option value="">Select domain</option>
          {domainOptions.map((d) => (
            <option key={d.domain_id} value={d.domain_id}>{d.display_name}</option>
          ))}
        </select>
      </label>
      <label style={labelStyle}>
        Track
        <select
          value={trackId}
          onChange={(e) => setTrackId(e.target.value)}
          style={selectStyle}
          disabled={!domainId}
        >
          <option value="">All tracks</option>
          {tracks.map((t) => {
            const id = t.track_id || t.id;
            return <option key={id} value={id}>{t.display_name || id}</option>;
          })}
        </select>
      </label>
      <label style={labelStyle}>
        Module
        <select
          value={moduleId}
          onChange={(e) => setModuleId(e.target.value)}
          style={selectStyle}
          disabled={!trackId}
        >
          <option value="">All modules</option>
          {modules.map((m) => {
            const id = m.module_id || m.id;
            return <option key={id} value={id}>{m.display_name || id}</option>;
          })}
        </select>
      </label>
      {requireScene !== false && (
        <label style={labelStyle}>
          Scene
          <select
            value={sceneId}
            onChange={(e) => setSceneId(e.target.value)}
            style={selectStyle}
            disabled={!domainId}
          >
            <option value="">Select scene</option>
            {scenes.map((s) => {
              const id = s.scene_id || s.id;
              return <option key={id} value={id}>{s.display_name || id}</option>;
            })}
          </select>
        </label>
      )}
    </div>
  );
}
